import { useCallback, useEffect, useRef } from 'react';
import { createSelector } from '@reduxjs/toolkit';

import { useIsTabAndWindowActive } from 'hooks/use-tab-active';
import { SIGNED_URL_EXPIRED_TIME } from 'constants/params';
import { RootState } from 'store/store';
import { useAppSelector, useAppDispatch, useAppStore } from 'store/store-hooks';
import { earliestSignedUrlExpiryMs } from 'components/app/_helpers/_app.helper';
import { updFileUrls } from 'components/app/_mediator/app-files.slice';
import { updThumbUrls } from 'components/app/_mediator/clip-thumb/clip-thumb.slice';
import { updFcUrls } from 'components/app/finalcuts/_mediator/fc-data.slice';

// Fallback/ceiling when nothing in the store carries a parseable expiry - matches the FE-requested file-url lifetime.
export const MAX_TIMEOUT = (SIGNED_URL_EXPIRED_TIME - 1) * 60 * 1000;
export const REFRESH_BUFFER_MS = 2 * 60 * 1000;
export const MIN_TIMEOUT = 30 * 1000;

export const scheduleDelay = (expiryMs: number | null): number =>
  expiryMs ? Math.max(MIN_TIMEOUT, Math.min(MAX_TIMEOUT, expiryMs - Date.now() - REFRESH_BUFFER_MS)) : MAX_TIMEOUT;

export const useAppUrls = () => {
  const selectedProject = useAppSelector((state) => state.projects.selectedProject);
  const isAudioPlaying = useAppSelector((state) => state.audio.isAudioPlaying);
  // Thumbnail URLs are signed server-side with a shorter, backend-controlled lifetime than file
  // URLs (which the FE explicitly requests at SIGNED_URL_EXPIRED_TIME). This tracks whichever
  // signed thumbnail/FC URL currently in the store expires soonest - regardless of whether it
  // landed there via this hook's own refresh or another fetcher (initial story/FC load, uploads) -
  // so the timer can be (re)scheduled off real expiry instead of a guessed interval.
  const nextThumbExpiryMs = useAppSelector(selectEarliestThumbExpiryMs);
  const isTabActive = useIsTabAndWindowActive();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const isReadyToRefresh = useRef(false);
  const listenTab = useRef(false);
  const timeout = useRef<NodeJS.Timeout>();
  const nextRefreshAt = useRef(Date.now() + MAX_TIMEOUT);

  const cb = useCallback(async () => {
    const { selectedStory, selectedFinalcut, selectedProject } = selectData(store.getState());
    if (!isReadyToRefresh.current) {
      listenTab.current = true;
      return;
    }
    if (!selectedProject || !selectedStory) return;
    await Promise.all([
      dispatch(updFileUrls(selectedProject)),
      dispatch(updThumbUrls(selectedStory)),
      selectedFinalcut && dispatch(updFcUrls(selectedFinalcut)),
    ]);
    // No explicit reschedule here - the nextThumbExpiryMs effect below picks up the fetch this
    // just landed in the store and reschedules off it.
  }, []);

  const wait = (delayMs: number) => {
    clearTimeout(timeout.current);
    nextRefreshAt.current = Date.now() + delayMs;
    timeout.current = setTimeout(cb, delayMs);
  };

  useEffect(() => {
    if (!selectedProject) return;
    wait(scheduleDelay(nextThumbExpiryMs));
    return () => clearTimeout(timeout.current);
  }, [selectedProject, nextThumbExpiryMs]);

  useEffect(() => {
    isReadyToRefresh.current = isTabActive && !isAudioPlaying;
    if (!isTabActive || isAudioPlaying) return;

    // Timer may be throttled while idle; also refresh when overdue on focus.
    const overdue = Date.now() >= nextRefreshAt.current;
    if (!listenTab.current && !overdue) return;

    listenTab.current = false;
    cb();
  }, [isTabActive, isAudioPlaying]);
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { selectedProject, selectedStory, selectedFinalcut } = state.projects;
  return { selectedProject, selectedStory, selectedFinalcut };
};

const selectEarliestThumbExpiryMs = createSelector(
  (state: RootState) => state.clipThumb.clipThumbnails,
  (state: RootState) => state.fc__data.fcClipsMap,
  (clipThumbnails, fcClipsMap) =>
    earliestSignedUrlExpiryMs([
      ...Object.values(clipThumbnails).map((t) => t.url),
      ...Object.values(fcClipsMap).map((c) => c.thumbnailUrl),
    ])
);
